import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';

const RegisterDeliveryModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  contracts = [], 
  suppliers = [], 
  tanks = [],
  loading = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    contract_id: '',
    tank_id: '',
    supplier_id: '',
    delivery_date: new Date(),
    quantity_liters: '',
    ddt_number: '',
    ddt_date: null,
    price_per_liter: '',
    invoice_number: '',
    invoice_date: null,
    notes: ''
  });

  const [selectedContract, setSelectedContract] = useState(null);

  useEffect(() => {
    if (!open) {
      setFormData({
        contract_id: '',
        tank_id: '',
        supplier_id: '',
        delivery_date: new Date(),
        quantity_liters: '',
        ddt_number: '',
        ddt_date: null,
        price_per_liter: '',
        invoice_number: '',
        invoice_date: null,
        notes: ''
      });
      setSelectedContract(null);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContractChange = (event, newValue) => {
    setSelectedContract(newValue);
    setFormData(prev => ({
      ...prev,
      contract_id: newValue?.id || ''
    }));
  };

  const handleSubmit = () => {
    if (!formData.contract_id || !formData.quantity_liters || !formData.delivery_date) {
      return;
    }

    const formatDate = (date) => date ? date.toISOString().split('T')[0] : null;

    onSubmit({
      ...formData,
      delivery_date: formatDate(formData.delivery_date),
      ddt_date: formatDate(formData.ddt_date),
      invoice_date: formatDate(formData.invoice_date)
    });
  };

  const getContractLabel = (contract) => {
    if (!contract) return '';
    const customer = contract.customer || {};
    return `${contract.cod_con || contract.id} - ${contract.nome || customer.nome || 'N/D'} - ${contract.localita || contract.citta || ''}`;
  };

  // Calcolo importi
  const quantity = parseFloat(formData.quantity_liters) || 0;
  const price = parseFloat(formData.price_per_liter) || 0;
  const imponibile = quantity * price;
  const iva = imponibile * 0.22;
  const totale = imponibile + iva;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Registra Rifornimento</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Contratto/Impianto */}
            <Grid item xs={12}>
              <Autocomplete
                options={contracts}
                getOptionLabel={getContractLabel}
                value={selectedContract}
                onChange={handleContractChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Impianto/Contratto *" 
                    placeholder="Cerca per codice, nome o località..."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            {/* Data consegna */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Data consegna *"
                value={formData.delivery_date}
                onChange={(date) => setFormData(prev => ({ ...prev, delivery_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Quantità */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quantità *"
                name="quantity_liters"
                type="number"
                value={formData.quantity_liters}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Litri</InputAdornment>
                }}
              />
            </Grid>

            {/* Fornitore */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Fornitore</InputLabel>
                <Select
                  name="supplier_id"
                  value={formData.supplier_id}
                  onChange={handleChange}
                  label="Fornitore"
                >
                  <MenuItem value="">
                    <em>Seleziona fornitore</em>
                  </MenuItem>
                  {suppliers.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Serbatoio */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Serbatoio</InputLabel>
                <Select
                  name="tank_id"
                  value={formData.tank_id}
                  onChange={handleChange}
                  label="Serbatoio"
                >
                  <MenuItem value="">
                    <em>Seleziona serbatoio</em>
                  </MenuItem>
                  {tanks.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.matricola} - {t.capacita}L
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* DDT */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numero DDT"
                name="ddt_number"
                value={formData.ddt_number}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Data DDT"
                value={formData.ddt_date}
                onChange={(date) => setFormData(prev => ({ ...prev, ddt_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Prezzo */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Prezzo unitario"
                name="price_per_liter"
                type="number"
                value={formData.price_per_liter}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/L</InputAdornment>
                }}
                inputProps={{ step: 0.001 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Imponibile"
                value={imponibile.toFixed(2)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  readOnly: true
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Totale (IVA 22%)"
                value={totale.toFixed(2)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  readOnly: true
                }}
              />
            </Grid>

            {/* Fattura */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numero Fattura"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Data Fattura"
                value={formData.invoice_date}
                onChange={(date) => setFormData(prev => ({ ...prev, invoice_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                name="notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.contract_id || !formData.quantity_liters}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Registra
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterDeliveryModal;
